import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  DefaultExpenseFilter,
  DefaultExpenseTypeResponse,
  GetExpenseTypeForEditRequest,
  PagedDefaultExpenseTypes,
  ProductIds,
  getDefaultExpenseTypes,
} from 'core'
import { UserRoles } from 'core/application/account/userRoles'

import TableV2 from 'common/components/dataDisplay/TableV2'

import { Button, Col, DefaultInputV2, DefaultSelectV2, Row, TablePagination } from 'common/components'
import { Card, Icon } from 'common/components'
import { useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import UpsertExpenseTypeModal from './UpsertExpenseTypeModal'

const DefaultExpenseTypes = () => {
  const { t: translate } = useTranslation()

  const {
    data: defaultExpenseTypes,
    isLoading: isLoadingTypes,
    sendQuery: sendGetDefaultExpenseTypes,
  } = useQueryRequestV2<PagedDefaultExpenseTypes>()

  const [filter, setFilter] = useState<DefaultExpenseFilter>({
    pageSize: 25,
    pageNumber: 1,
    productId: 1,
    categoryId: null,
  })

  const [forEditRequest, setForEditRequest] = useState<GetExpenseTypeForEditRequest | null>(null)
  const [modalIsVisible, setModalIsVisible] = useState(false)

  const { user } = useLoginUserV2()
  const isSuperAdmin = user.roles.includes(UserRoles.SuperAdmin)

  useEffect(() => {
    sendGetDefaultExpenseTypes(() => getDefaultExpenseTypes(filter))
  }, [filter])

  const renderStatusIcon = (dataIndex: string) => (_: string, record: DefaultExpenseTypeResponse) =>
    record[dataIndex] ? (
      <Icon key={record.Code} type={'far fa-check-circle color-green-6'} fontAwesome />
    ) : (
      <Icon key={record.Code} type={'far fa-times-circle color-red-6'} fontAwesome />
    )

  const onChangeHandler = (currentPagination: TablePagination) => {
    setFilter({
      ...filter,
      pageNumber: currentPagination.current,
      pageSize: currentPagination.pageSize,
    })
  }

  const handleModalState = (forEditRequest: GetExpenseTypeForEditRequest | null = null, isVisible: boolean = false) => {
    setForEditRequest(forEditRequest)
    setModalIsVisible(isVisible)
  }

  return (
    <>
      <Card title={translate('csi-dashboard.expense-types.title')}>
        <section>
          <Formik
            enableReinitialize={true}
            validationSchema={Yup.object({})}
            initialValues={filter}
            onSubmit={(values) => {
              setFilter({
                ...filter,
                pageNumber: 1,
                productId: values.productId,
                q: values.q,
              })
            }}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <Row gutter={16}>
                  <Col span={8}>
                    <DefaultInputV2
                      suffix={<Icon type="fal fa-search" fontAwesome />}
                      onKeyUp={async (e: any) => {
                        if (e.keyCode == 13) await formik.submitForm()
                      }}
                      className="mr-8"
                      placeholder="Search by name"
                      name="q"
                    />
                  </Col>
                  <Col span={4}>
                    <DefaultSelectV2
                      name="productId"
                      placeholder="Select product"
                      label={null}
                      items={[
                        { id: ProductIds.IchraId, name: 'ICHRA' },
                        { id: ProductIds.QsehraId, name: 'QSEHRA' },
                        { id: ProductIds.GroupHraId, name: 'GCHRA' },
                      ]}
                    />
                  </Col>
                  <Col span={8}>
                    <Button
                      type="primary"
                      className="mr-16"
                      htmlType="submit"
                      disabled={isLoadingTypes}
                      loading={isLoadingTypes}
                    >
                      {translate('general.search')}
                    </Button>

                    <Button
                      htmlType={'button'}
                      onClick={() =>
                        setFilter({
                          ...filter,
                          productId: ProductIds.IchraId,
                          q: null,
                          catetgoryId: null,
                        })
                      }
                      disabled={isLoadingTypes}
                      loading={isLoadingTypes}
                    >
                      Reset Filter
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      htmlType="button"
                      loading={isLoadingTypes}
                      disabled={isLoadingTypes}
                      onClick={() => handleModalState(null, true)}
                    >
                      Add Expense Type
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        </section>

        <TableV2
          dataSource={defaultExpenseTypes?.items}
          columns={[
            {
              title: 'Id',
              dataIndex: 'id',
              key: 'id',
            },
            {
              title: 'Name',
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: 'Code',
              dataIndex: 'code',
              key: 'code',
            },
            {
              title: 'Active',
              dataIndex: 'active',
              key: 'active',
              align: 'center',
              render: renderStatusIcon('active'),
            },
            {
              title: 'HRA Eligible',
              dataIndex: 'limitedHraEligible',
              key: 'limitedHraEligible',
              align: 'center',
              render: renderStatusIcon('limitedHraEligible'),
            },
            ...(isSuperAdmin
              ? [
                  {
                    title: 'AI Auto-verify',
                    dataIndex: 'aiAutoVerifyEnabled',
                    key: 'aiAutoVerifyEnabled',
                    align: 'center' as 'center',
                    render: renderStatusIcon('aiAutoVerifyEnabled'),
                  },
                ]
              : [{}]),
            {
              title: 'Action',
              key: 'action',
              align: 'center',
              render: (_: string, { id: expenseTypeId }: DefaultExpenseTypeResponse) => (
                <Button
                  key={expenseTypeId}
                  onClick={() =>
                    handleModalState(
                      {
                        expenseTypeId,
                        productId: filter.productId,
                      },
                      true,
                    )
                  }
                >
                  Edit
                </Button>
              ),
            },
          ]}
          loading={isLoadingTypes}
          pagination={{
            total: defaultExpenseTypes?.totalItemCount,
            pageSize: defaultExpenseTypes?.pageSize,
            current: defaultExpenseTypes?.pageNumber,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '25', '30', '40'],
          }}
          onChange={onChangeHandler}
        />
      </Card>
      <UpsertExpenseTypeModal
        visible={modalIsVisible}
        forEditRequest={forEditRequest}
        isSuperAdmin={isSuperAdmin}
        onCancel={() => handleModalState()}
        onSaved={(productId: number, isEdit: boolean) => {
          const lastPage = Math.ceil(defaultExpenseTypes?.totalItemCount / filter.pageSize)
          setFilter({
            ...filter,
            pageNumber: isEdit ? filter.pageNumber : lastPage,
            productId: productId,
          })
          handleModalState()
        }}
      />
    </>
  )
}

export default DefaultExpenseTypes
